import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DescriptionOutlined,
  DataObjectOutlined,
  Refresh,
  Save,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getAtendabotPromptTemplate,
  updateAtendabotPromptTemplate,
} from '../services/api';
import './AtendabotTemplatePage.css';

const formatUpdatedAt = (value) => {
  if (!value) return 'Ainda não informado';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Ainda não informado';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const AtendabotTemplatePage = () => {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [variables, setVariables] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [regeneration, setRegeneration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const editorRef = useRef(null);

  const hasChanges = content !== originalContent;
  const characterCount = content.length;
  const usedVariables = useMemo(() => new Set(
    Array.from(content.matchAll(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g))
      .map((match) => match[1]),
  ), [content]);

  const loadTemplate = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await getAtendabotPromptTemplate();
      const nextContent = String(data?.content || '');
      setContent(nextContent);
      setOriginalContent(nextContent);
      setVariables(Array.isArray(data?.available_variables) ? data.available_variables : []);
      setUpdatedAt(data?.updated_at || null);
      setRegeneration(null);
    } catch (error) {
      console.error('Erro ao carregar template do Atendabot:', error);
      const message = error?.response?.data?.error || 'Não foi possível carregar o template.';
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const insertVariable = (variable) => {
    const token = `{{${variable}}}`;
    const editor = editorRef.current;
    const start = editor?.selectionStart ?? content.length;
    const end = editor?.selectionEnd ?? start;
    const nextContent = `${content.slice(0, start)}${token}${content.slice(end)}`;
    setContent(nextContent);

    window.requestAnimationFrame(() => {
      editor?.focus();
      const nextPosition = start + token.length;
      editor?.setSelectionRange(nextPosition, nextPosition);
    });
  };

  const requestSave = () => {
    if (content.trim().length < 100) {
      toast.warning('O template deve possuir pelo menos 100 caracteres.');
      return;
    }
    setConfirmOpen(true);
  };

  const saveTemplate = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const data = await updateAtendabotPromptTemplate(content);
      const savedContent = String(data?.content || content);
      setContent(savedContent);
      setOriginalContent(savedContent);
      setUpdatedAt(data?.updated_at || null);
      setVariables(Array.isArray(data?.available_variables) ? data.available_variables : variables);
      setRegeneration(data?.regeneration || null);

      if (data?.regeneration?.failed_count > 0) {
        toast.warning('Template salvo, mas alguns restaurantes não puderam ser atualizados.');
      } else {
        toast.success('Template salvo e prompts publicados atualizados.');
      }
    } catch (error) {
      console.error('Erro ao salvar template do Atendabot:', error);
      toast.error(error?.response?.data?.error || 'Não foi possível salvar o template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="atendabot-template-page">
      <Box className="atendabot-template-page__header">
        <Box>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box className="atendabot-template-page__icon">
              <DescriptionOutlined fontSize="small" />
            </Box>
            <Typography variant="h5" component="h1" fontWeight={700}>
              Template do Atendabot
            </Typography>
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Edite a estrutura global usada para gerar o prompt personalizado de cada restaurante.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadTemplate}
            disabled={loading || saving}
          >
            Recarregar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
            onClick={requestSave}
            disabled={loading || saving || !hasChanges}
          >
            Salvar e aplicar
          </Button>
        </Stack>
      </Box>

      <Alert severity="info" className="atendabot-template-page__notice">
        Ao salvar, os prompts publicados são regenerados. Restaurantes configurados para usar um prompt manual do Manager não são alterados.
      </Alert>

      {regeneration && (
        <Alert severity={regeneration.failed_count > 0 ? 'warning' : 'success'}>
          {regeneration.updated_count} prompt(s) atualizado(s), {regeneration.skipped_count} ignorado(s)
          {regeneration.failed_count > 0 ? ` e ${regeneration.failed_count} com falha.` : '.'}
          {regeneration.error ? ` ${regeneration.error}` : ''}
        </Alert>
      )}

      {loadError && (
        <Alert severity="error" action={<Button onClick={loadTemplate}>Tentar novamente</Button>}>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box className="atendabot-template-page__loading">
          <CircularProgress />
        </Box>
      ) : !loadError && (
        <Box className="atendabot-template-page__grid">
          <Paper className="atendabot-template-page__editor-card" elevation={0}>
            <Box className="atendabot-template-page__editor-meta">
              <Typography variant="body2" color="text.secondary">
                Última atualização: {formatUpdatedAt(updatedAt)}
              </Typography>
              <Typography variant="body2" color={hasChanges ? 'warning.main' : 'text.secondary'}>
                {characterCount.toLocaleString('pt-BR')} caracteres
                {hasChanges ? ' • alterações não salvas' : ''}
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={28}
              value={content}
              onChange={(event) => setContent(event.target.value)}
              inputRef={editorRef}
              className="atendabot-template-page__editor"
              aria-label="Conteúdo do template do Atendabot"
            />
          </Paper>

          <Paper className="atendabot-template-page__variables-card" elevation={0}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DataObjectOutlined color="primary" fontSize="small" />
              <Typography variant="h6" fontWeight={700}>Variáveis</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Clique para inserir no ponto atual do editor. Variáveis desconhecidas são bloqueadas ao salvar.
            </Typography>
            <Box className="atendabot-template-page__variables-list">
              {variables.map((variable) => (
                <Chip
                  key={variable}
                  label={`{{${variable}}}`}
                  size="small"
                  variant={usedVariables.has(variable) ? 'filled' : 'outlined'}
                  color={usedVariables.has(variable) ? 'primary' : 'default'}
                  onClick={() => insertVariable(variable)}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aplicar o template global?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            O novo conteúdo será salvo no banco e os prompts já publicados serão regenerados para os restaurantes elegíveis.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveTemplate}>Salvar e aplicar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AtendabotTemplatePage;
